
import React, {
  lazy, Suspense, useCallback, useEffect, useState
} from 'react';
import { Provider, useDispatch } from 'react-redux';
import {
  BrowserRouter, Route, Navigate, useNavigate, Routes,
} from 'react-router-dom';
import {useTranslation, I18nextProvider} from 'react-i18next';
import {
  CssBaseline,
  StyledEngineProvider,
  createTheme,
} from '@mui/material';
import { ThemeProvider, Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import * as api from './api';
import {
  loginAction, logoutAction, signupAction,
  resumeLastSessionAction, wxloginAction,
} from '@ivymobi/orbit-core/business/user';
import configureStore from '@ivymobi/orbit-core/business/configureStore';
import { debounce, isEmpty, isNull } from 'lodash';
import { ThemeContext } from './orbit-context';
import themeConfig from './services/theme';
import { updateCSSVariables } from './lib/theme-sync';
import Loader from './components/loaders';
import Notification from './layout/notification';
import ErrorBoundary from './components/errorBoundles';
import User from './users';
import UserV2 from './usersV2';
import Invitations from './users/invitations';
import WechatCode from './users/wechatCode';
import { ShareProductDetailView } from './shares/shareProductDetail';
import RedirectContainer from './users/RedirectContainer';
import ResetPhone from './users/ResetPhone';
import { showNotification } from '@ivymobi/orbit-core/business/notification';
import { xorEncryptDecrypt } from './utils/settings';
import PreProcess from './components/preProcess';
import i18n from './services/i18n';
import MessageCenter from './components/MeesageCentter';
import WXDownloadMaskView from './components/WXDownloadMaskView';
import { isMobile, UserResponse } from '@ivymobi/orbit-core';
import { DeviceType } from '@ivymobi/orbit-core/orbit-context';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const ActiveUserContainer = lazy(
  () => import('./user' /* webpackChunkName: "user" */)
);

const Share = lazy(
  () => import('./shares' /* webpackChunkName: "share" */)
);

const D3DView = lazy(
  () => import('./shares/D3DView')
);

const MobilePDF=lazy(() => import('./wechatPDF'));

const MobileDocPDF=lazy(() => import('./wechatDocPDF'));

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      //position: 'relative',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      fontSize:'0.875rem',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.getContrastText(
        theme.palette.background.default
      ),
    },
  })
);

const Main = () => {
  const [t] = useTranslation();

  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);

  useEffect(() => {
    dispatch(resumeLastSessionAction()).then(
      (userData) => {
        userData.logout = logout;
        setUser(userData);
      },
      () => setUser({}));
  }, []);

  const doLogin = (userData) => {
    userData.logout = logout;
    setUser(userData);
  };

  const wechatCodeLogin = useCallback((loginData) => {
    dispatch(wxloginAction(loginData))
    .then(
      (userData) => {
        doLogin(userData);
        window.parent.window.setUser1(userData);
      },
      (err) => {
        window.parent.window.onLoginError(err);
        console.error(err);
      }
    );
  }, [dispatch]);

  const login = useCallback((loginData) => {
    dispatch(loginAction(loginData))
    .then(
      (userData) => {
        userData.from = loginData.from;
        // 单点登录改造： 手机验证码登录后  修改localstorage中的内容：
        localStorage.setItem('SSOLOGIN', 'false');
        doLogin(userData);
        navigate('/', { replace: true });
      },
      (err) => {
        const message = err?.message ?? 'Retry';
        dispatch(showNotification(t(message),  'error'));
      }
    );
  }, [dispatch]);

  const loginByPassword = useCallback(
    (loginData: {account: string; password: string}) => {
      const params = {
        url: 'user/login/account',
        account: loginData.account,
        password: xorEncryptDecrypt(loginData.password)
      };

      dispatch(loginAction(params))
      .then(
        (userData) => {
          localStorage.setItem('SSOLOGIN', 'false');
          doLogin(userData);
          navigate('/', { replace: true });
        },
        (err) => {
          const message = err?.message ?? 'Retry';
          dispatch(showNotification(t(message),  'error'));
        }
      );
    },
    [dispatch]);

  const signup = useCallback((signupData) => {
    dispatch(signupAction(signupData))
    .then(
      (userData) => {
        doLogin(userData);
        navigate('/', { replace: true });
      },
      (err) => {
        console.error(err);
      });
  }, [dispatch, navigate]);

  // 退出登录加个判断，根据tenantID判断是不是ABB团队，跳转到ABB首页
  const logout = useCallback((options?: { skipAbbRedirect?: boolean }) => {
    return dispatch(logoutAction())
    .then(() => {
      const localTenantId = localStorage.getItem('tenantId');
      const isABBtenant = localTenantId === '20243346-9db5-42bf-a52c-b5d800f5fdd4';
      const env = process.env.NODE_ENV || 'production';
      // 测试地址 https://abbdrives.ivymobi.com.cn
      // 生产地址： https://abbdrives.yangben.cn
      const abbURL = env === 'development' ? 'https://abbdrives.ivymobi.com.cn' : 'https://abbdrives.yangben.cn';

      if (isABBtenant && !options?.skipAbbRedirect) {
        window.location.href = abbURL;
      } else {
        setUser({});
      }
    });
  }, [dispatch, setUser]);

  const acceptInvitation = async (id, data) => {
    if (user.token) {
      return api.postJSON<UserResponse>({
        url: `tenants/invitations/${id}`,
        token: user.token,
        body: {
          ...data
        }
      }).then(
        (res) => res,
        (err) => {
          dispatch(
            showNotification(t(err.message ?? 'Retry'),  'error')
          );
        }
      );
    } else {
      return api.postJSON<UserResponse>({
        url: `tenants/invitations/code/${id}`,
        body: {
          ...data
        }
      }).then(
        (res) => {
          if (!isEmpty(res)) {
            const { token, ...user } = res;
            localStorage.setItem('token', token);
            doLogin(user);
          }
          return res;
        },
        (err) => {
          console.log('err========', err);
          dispatch(
            showNotification(t(err.message ?? 'Retry'),  'error')
          );
        }
      );
    }
  };

  if (isNull(user)) {
    return (<Loader />);
  }

  return (
    <div className={classes.root}>
      <Routes>
        {/* 单点登录跳转页面， 目前只有ABB一个团队有相关功能。怎么做到更通用还需要考虑 */}
        <Route path="/SSORedirect/:user" element={<RedirectContainer user={user} logout={logout} />} />
        <Route path="/SSORedirect" element={<RedirectContainer user={user} logout={logout} />} />
        <Route path="/user/*" element={
          (user && user.token)
            ? <Navigate to="/" replace />
            : <UserV2
              login={login}
              signup={signup}
              loginByPassword={loginByPassword}
            />
        } />
        <Route path="/invitations/:invitationId" element={<Invitations user={user} acceptInvitation={acceptInvitation} />} />
        <Route path="/resetPhone/:inv_id" element={<ResetPhone />} />
        <Route path="/shares/:id" element={
          <ErrorBoundary>
            <Suspense fallback={<Loader />}>
              <Share user={user} onLogin={doLogin} />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/3d" element={
          <ErrorBoundary>
            <Suspense fallback={<Loader />}>
              <D3DView />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/mobilePdf/:id" element={
          <ErrorBoundary>
            <Suspense fallback={<Loader />}>
              <MobilePDF />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/mobilePdf/:tid/:docid" element={
          <ErrorBoundary>
            <Suspense fallback={<Loader />}>
              <MobileDocPDF />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/share/ProductDetail/:shareId/:id" element={<ShareProductDetailView />} />
        {/*扫码登录成功回调*/}
        <Route path="/wxlogin" element={<WechatCode user={user} login={wechatCodeLogin} />} />
        {!isEmpty(user) && <Route path="/*" element={
          <Suspense fallback={<Loader />}>
            <ActiveUserContainer user={user} logout={logout} />
          </Suspense>
        } />}
        <Route path="*" element={<Navigate to="/user/signin" replace />} />
      </Routes>
    </div>
  );
};


export const ThemeComponents = () => {
  const [mainColor, setMainColor] = useState('#007aff');
  themeConfig.palette.primary.main = mainColor;
  const theme = createTheme(themeConfig);

  const handleChangeTheme = useCallback((color) => {
    if (color === mainColor) return;
    setMainColor(color);
    // 同步更新 Tailwind CSS 变量，使 V2 组件跟随 VI 色
    updateCSSVariables(color);
  }, [mainColor]);

  // 初始化时同步默认色到 CSS 变量
  useEffect(() => {
    updateCSSVariables(mainColor);
  }, []);

  const [deviceType, setDeviceType] = useState<'mobile'| 'computer'>('computer');

  useEffect(() => {
    const handleResize = debounce(() => {
      const currentWidth = window.innerWidth;
      if (isMobile()) {
        setDeviceType('mobile');
      }
      else if (currentWidth <= 766) {
        setDeviceType('mobile');
      } else {
        setDeviceType('computer');
      }
    });

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return (

    <ThemeContext.Provider value={handleChangeTheme}>
      <CssBaseline />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <DeviceType.Provider value={deviceType}>
            <PreProcess>
              <I18nextProvider i18n={i18n}>
                <MessageCenter>
                  <WXDownloadMaskView>
                    <Main />
                  </WXDownloadMaskView>
                </MessageCenter>
                <Notification />
              </I18nextProvider>
            </PreProcess>
          </DeviceType.Provider>

        </ThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>

  );
};

export default function OrbitApp() {
  const store = configureStore(api, {});

  return (
    <BrowserRouter>
      <Provider store={store}>
        <ThemeComponents/>
      </Provider>
    </BrowserRouter>
  );
}
